import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useDeleteFjJobMutation,
  useGetFjJobQuery,
  useSaveFjJobMutation,
} from '../../../../../../redux/services/fj_jobs.jsx';
import { Spinner } from '../../../../../common/index.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { useGetStudyProgramsQuery } from '../../../../../../redux/services/study_programs.jsx';
import { useGetFjJobsTypesQuery } from '../../../../../../redux/services/fj_jobs_types.jsx';
import FjJobForm from './FjJobForm.jsx';
import { useSelector } from 'react-redux';
import { useGetExternalPartnersQuery } from '../../../../../../redux/services/externalPartners.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';

const FjJobsEdit = ({ isCreate }) => {
  const user = useSelector((state) => state.auth.user);
  const isFriJobsAdmin = user && user.permissions['fri-jobs-admin'];
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams();
  const { t } = useTranslation();
  const {
    saveSuccess,
    deleteSuccess: showDeleteSuccess,
    errorMessage: errorMessage,
  } = useToastMessages();

  // Derive archive mode from query string (?getFromArchive)
  const archiveMode = new URLSearchParams(location.search).has(
    'getFromArchive',
  );

  // Create pageTitle constant based on mode
  const pageTitle = isCreate
    ? t('Pridanie pracovnej ponuky')
    : archiveMode
      ? t('Obnovenie pracovnej ponuky z archívu')
      : t('Úprava pracovnej ponuky');

  // Set document title
  useEffect(() => {
    document.title = `${pageTitle} - FRI.jobs`;
  }, [pageTitle]);

  // Data for editing existing job
  const { data: jobData, isLoading: isLoadingJobData } = useGetFjJobQuery(
    jobId,
    {
      skip: !jobId,
    },
  );

  const { data: studyPrograms } = useGetStudyProgramsQuery();
  const { data: jobsTypes } = useGetFjJobsTypesQuery();
  const { data: partners } = useGetExternalPartnersQuery(true, {
    skip: !isFriJobsAdmin,
  });
  const [
    deleteJob,
    {
      isSuccess: isDeleteJobSuccess,
      isError: isDeleteJobError,
      error: deleteJobError,
    },
  ] = useDeleteFjJobMutation();
  const [saveJob] = useSaveFjJobMutation();

  const handleSaveJob = (values) => {
    saveJob({
      external_partner_id: values.external_partner_id
        ? values.external_partner_id
        : user.external_partner_id,
      id: isCreate ? undefined : archiveMode ? undefined : jobId,
      ...values,
    })
      .unwrap()
      .then(() => {
        saveSuccess();
        navigate(-1);
      });
  };

  useEffect(() => {
    if (isDeleteJobSuccess) {
      showDeleteSuccess();
      navigate(-1);
    } else if (isDeleteJobError) {
      errorMessage(deleteJobError);
    }
  }, [isDeleteJobSuccess, isDeleteJobError, deleteJobError]);

  const initialValuesForForm = {
    study_programs_id: jobData
      ? jobData.study_programs.map((study_program) => study_program.id)
      : null,
    job_types_id: jobData
      ? jobData.job_types.map((job_type) => job_type.id)
      : null,
    is_interest_notification_enabled: true,
    ...jobData,
  };

  return isLoadingJobData ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={-1}
        onDelete={!isCreate ? () => deleteJob(jobId) : undefined}
        permissionsBasic={'fri-jobs-jobs'}
      />
      <div style={{ margin: '10px' }}>
        <FjJobForm
          initialValues={initialValuesForForm}
          isCreate={isCreate}
          archiveMode={archiveMode}
          onSubmit={(values) => handleSaveJob(values)}
          studyPrograms={studyPrograms}
          jobsTypes={jobsTypes}
          partners={partners}
        />
      </div>
    </>
  );
};

FjJobsEdit.prototype = {
  isCreate: PropTypes.bool.isRequired,
  archiveMode: PropTypes.bool,
};

export default FjJobsEdit;
